import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { api } from "../api.js";
import type { Me } from "../types.js";
import {
  IconDashboard, IconDevice, IconWrench, IconPackage, IconCart,
  IconTag, IconUsers, IconCrew, IconHistory, IconCertificate,
} from "../icons.js";

const NAV = [
  { to: "/", label: "Übersicht", end: true, icon: IconDashboard },
  { to: "/geraete", label: "Geräte", icon: IconDevice },
  { to: "/service", label: "Service & Prüfungen", icon: IconWrench },
  { sep: "Beschaffung" },
  { to: "/ersatzteile", label: "Ersatzteile", icon: IconPackage },
  { to: "/bestellungen", label: "Bestellungen", icon: IconCart },
  { sep: "Verwaltung", adminOnly: true },
  { to: "/preise", label: "Preise", adminOnly: true, icon: IconTag },
  { to: "/benutzer", label: "Benutzer", adminOnly: true, icon: IconUsers },
  { to: "/kolonnen", label: "Kolonnen", adminOnly: true, icon: IconCrew },
  { sep: "Nachweise" },
  { to: "/zertifizierungen", label: "Zertifizierungen", icon: IconCertificate },
  { to: "/historie", label: "Historie", icon: IconHistory },
] as const;

export function roleLabel(role: string) {
  return role === "ADMIN" ? "Administrator" : "Mitarbeiter";
}

export default function Shell({ me, onSignedOut, children }:
  { me: Me; onSignedOut: () => void; children: ReactNode }) {

  const initials = me.name.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const nav = NAV.filter((n) => !("adminOnly" in n && n.adminOnly) || me.role === "ADMIN");

  return (
    <div className="shell">
      <aside className="rail">
        <div className="rail-head">
          <img src="/airjet-logo.svg" alt="AirJet" className="brand-logo" />
        </div>
        <nav className="nav">
          {nav.map((n, i) => {
            if ("sep" in n) return <div className="nav-sep" key={i}>{n.sep}</div>;
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={"end" in n ? n.end : false}
                       className={({ isActive }) => (isActive ? "on" : "")}>
                <Icon /> {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="rail-foot">
          <span className="avatar">{initials}</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <b style={{ fontSize: 12.5, display: "block", lineHeight: 1.25 }}>{me.name}</b>
            <small style={{ fontSize: 10.5, color: "var(--ink-3)" }}>{roleLabel(me.role)}</small>
          </span>
          <button className="btn ghost sm" title="Abmelden"
                  onClick={async () => { await api.post("/api/auth/logout"); onSignedOut(); }}>
            Abmelden
          </button>
        </div>
      </aside>
      <div className="main">{children}</div>
    </div>
  );
}

export function Page({ title, sub, actions, children }:
  { title: string; sub?: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <>
      <header className="topbar">
        <div><h1>{title}</h1>{sub && <div className="sub">{sub}</div>}</div>
        <div className="spacer" />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>{actions}</div>
      </header>
      <main className="content">{children}</main>
    </>
  );
}

export function Pill({ status, children }: { status: string; children: ReactNode }) {
  const cls = status === "CRITICAL" ? "crit" : status === "WARNING" ? "warn"
            : status === "OK" ? "ok" : status;
  return <span className={`pill ${cls}`}>{children}</span>;
}

export function statusLabel(s: string) {
  return s === "CRITICAL" ? "Handlungsbedarf" : s === "WARNING" ? "Beobachten" : "Einsatzbereit";
}
